/**
 * Geofence API Service
 * Handles CRUD operations for user-defined geofences
 */

import axios, { AxiosInstance } from 'axios';

// In development, use relative URLs to work with React proxy
// In production, use the environment variable or default to /api
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || '/api');

export interface GeoJSONGeometry {
  type: 'Polygon';
  coordinates: number[][][]; // [[[lng, lat], [lng, lat], ...]]
}

export interface Geofence {
  id: number;
  name: string;
  description?: string;
  geometry: GeoJSONGeometry;
  created_by?: string;
  created_at: string;
  updated_at: string;
  course_id?: string;
  active: boolean;
}

export interface CreateGeofenceParams {
  name: string;
  description?: string;
  geometry: GeoJSONGeometry;
  course_id?: string;
}

export interface UpdateGeofenceParams {
  name?: string;
  description?: string;
  geometry?: GeoJSONGeometry;
  active?: boolean;
}

class GeofenceApiService {
  private client: AxiosInstance;

  constructor() {
    // Remove trailing slash if present
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    
    this.client = axios.create({
      baseURL: `${baseUrl}/geofences`,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add authentication interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Create a new geofence
   */
  async createGeofence(params: CreateGeofenceParams): Promise<Geofence> {
    try {
      const response = await this.client.post('', params);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create geofence');
    }
  }

  /**
   * List all geofences
   */
  async listGeofences(params?: {
    course_id?: string;
    active_only?: boolean;
  }): Promise<Geofence[]> {
    try {
      // Add timestamp to prevent browser caching of API responses
      const requestParams = { ...params, _t: new Date().getTime() };
      const response = await this.client.get('', { params: requestParams });
      return response.data;
    } catch (error: any) {
      const statusCode = error.response?.status;
      const detail = error.response?.data?.detail;
      
      
      // Return empty array if table doesn't exist yet (graceful degradation)
      if (statusCode === 500 && detail?.includes('relation "geofences" does not exist')) {
        return [];
      }
      
      // Return empty array for other errors too (don't block app loading)
      return [];
    }
  }

  /**
   * Update a geofence
   */
  async updateGeofence(id: number, params: UpdateGeofenceParams): Promise<Geofence> {
    try {
      const response = await this.client.put(`/${id}`, params);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update geofence');
    }
  }

  /**
   * Delete a geofence
   */
  async deleteGeofence(id: number): Promise<void> {
    try {
      await this.client.delete(`/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to delete geofence');
    }
  }

  /**
   * Convert a circle to a polygon geometry (for storage)
   * @param center - [lng, lat] coordinates of the circle center
   * @param radiusMeters - radius in meters
   * @param numPoints - number of points to approximate the circle (default 64)
   */
  circleToPolygon(center: [number, number], radiusMeters: number, numPoints: number = 64): GeoJSONGeometry {
    const [lng, lat] = center;
    const coordinates: number[][] = [];
    
    // Convert radius from meters to degrees (approximate)
    // At the equator, 1 degree ≈ 111,320 meters
    // Adjust for latitude
    const latRadians = lat * (Math.PI / 180);
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos(latRadians);
    
    const radiusLat = radiusMeters / metersPerDegreeLat;
    const radiusLng = radiusMeters / metersPerDegreeLng;
    
    // Generate points around the circle
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const pointLng = lng + radiusLng * Math.cos(angle);
      const pointLat = lat + radiusLat * Math.sin(angle);
      coordinates.push([pointLng, pointLat]);
    }
    
    return {
      type: 'Polygon',
      coordinates: [coordinates]
    };
  }
}

export const geofenceApi = new GeofenceApiService();
export default geofenceApi;
